import { FormControl, Grid, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import FormSection from "../../../../common/components/functional/formSection/FormSection";
import { YesNoOptions, YES_NO_OPTIONS } from "../constants";
import LabelSelectRow from "./LabelSelectRow";
import { useParams } from "react-router-dom";
import { VerticalAlignGrid } from "../../../invoices/styled";
import CustomAutocompleteMulti from "../../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { selectAllCustomerOrganizations } from "../../customerOrganizationsSlice";
import { useContractDataContext } from "../ContractDataProvider";

const MAIN_FORM_CUSTOMER_USING_FACTORING = "factoringCustomerOrgIds";

const InvoicingInvoicingSection = () => {
  const { t } = useTranslation("customers");
  const { customerCompanyId } = useParams();
  const {
    factoringProviderOptions,
    salesAccountsOptions,
    salesAccountsObject,
  } = useContractDataContext();
  const {
    getValues,
    watch,
    control,
    formState: { errors },
  } = useFormContext();

  const watchedFactoringEnabled = watch("factoringEnabled");

  const allCustomerOrganizations = useAppSelector((state) =>
    selectAllCustomerOrganizations(state),
  ).filter((org) => org.customerCompanyId === customerCompanyId);

  const factoringEnabled = watchedFactoringEnabled === YesNoOptions.Yes;

  return (
    <FormSection title={t("contract.factoringInvoicing")}>
      <Typography variant="secondary" padding="1rem">
        {t("contract.factoringInvoicingGuideText")}
      </Typography>
      <LabelSelectRow
        label={t("contract.factoringEnabled")}
        fieldName="factoringEnabled"
        options={YES_NO_OPTIONS}
        getDisplayValue={(val) => (val ? t(`common:${val}`) : "")}
        selectWidth={2}
      />
      {factoringEnabled && (
        <>
          <LabelSelectRow
            label={t("contract.factoringFinanceCompany")}
            fieldName="factoringContractId"
            options={factoringProviderOptions.map((option) => option.value)}
            getDisplayValue={(value) =>
              factoringProviderOptions.find((fp) => fp.value === value)
                ?.label || ""
            }
            required={factoringEnabled}
            selectError={!!errors.factoringContractId}
          />
          <LabelSelectRow
            label={t("contract.accountingOfInvoicing")}
            fieldName="factoringSalesAccountCode"
            options={
              salesAccountsOptions.length > 0
                ? salesAccountsOptions.map((account) => `${account.code}`)
                : [getValues("factoringSalesAccountCode")]
            }
            getDisplayValue={(code: string) =>
              `${code} ${salesAccountsObject[code]?.description || ""}`
            }
            showTooltip
            tooltipText={t("contract.accountingOfInvoicingTooltip")}
            required={factoringEnabled}
          />
          <VerticalAlignGrid container spacing={1}>
            <Grid
              container
              item
              xs={3}
              justifyContent="flex-end"
              paddingRight="0.5rem"
              textAlign="end"
            >
              <Typography variant="secondary">
                {t("contract.factoringUsingCustomers")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Controller
                  name={MAIN_FORM_CUSTOMER_USING_FACTORING}
                  control={control}
                  rules={{
                    required: factoringEnabled,
                    validate: (value) => {
                      if (value.length === 0) {
                        return "";
                      }
                      return true;
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <CustomAutocompleteMulti
                      defaultValue={value}
                      options={allCustomerOrganizations}
                      customInputLabel={t("contract.searchCustomers")}
                      customInputLabelProps={{ shrink: undefined }}
                      fullWidth
                      name={MAIN_FORM_CUSTOMER_USING_FACTORING}
                      onChange={(_: any, newValue: AutocompleteType[]) => {
                        const formattedValue = newValue.map((v) => ({
                          id: v.id,
                          name: v.name,
                        }));
                        onChange(formattedValue);
                      }}
                      textFieldError={
                        !!errors[MAIN_FORM_CUSTOMER_USING_FACTORING]
                      }
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          <VerticalAlignGrid container spacing={1}>
            <Grid item xs={3} />
            <Grid item xs={8}>
              <Typography sx={{ fontSize: "12px" }} variant="secondary">
                {t("contract.factoringUsingCustomersGuideText")}
              </Typography>
            </Grid>
          </VerticalAlignGrid>
        </>
      )}
    </FormSection>
  );
};

export default InvoicingInvoicingSection;
